// Light, monday.com-style theme (user preference: no dark UI). The overlay reads
// as a floating light tool panel via elevation + a distinct header + accessible
// borders, so it stays legible over any host. All colour pairings are verified
// for WCAG 2.2 AA in examples/plain-html/verify-a11y.py (text ≥4.5:1, non-text
// ≥3:1, focus ≥3:1) and survive forced-colors mode via borders + icons.
export const CSS = `
:host { all: initial; }
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:host {
  /* surfaces */
  --qc-canvas: #f6f7fb;      /* panel / drawer background */
  --qc-surface: #ffffff;     /* dialog, inputs, cards */
  --qc-surface-2: #eceef5;   /* unselected chips, accordion, subtle fills */
  /* borders — darkened past monday's defaults so interactive edges pass 3:1 on white */
  --qc-border: #8b90a6;      /* interactive boundaries (inputs, chips, segments) */
  --qc-divider: #d5d9e6;     /* decorative separators */
  /* text */
  --qc-text: #1c1f3b;        /* primary — ~14:1 on white */
  --qc-text-dim: #55596f;    /* secondary/small — ≥5:1 on white */
  /* action accent (owns every QC Lens action incl. Submit) */
  --qc-accent: #0073ea;      /* outlines, focus, selected borders */
  --qc-accent-strong: #005ec7;/* filled buttons/selected fills w/ white text (≥4.5:1) */
  --qc-accent-tint: #e5f0fd; /* selected chip subtle tint bg for hover */
  --qc-danger: #b42318;      /* blocker / errors — dark enough for text */
  --qc-warn: #9a6700;        /* major */
  --qc-on-accent: #ffffff;
  --qc-highlight: #0073ea;   /* picker highlight */
  --qc-highlight-fill: rgba(0, 115, 234, 0.10);
  --qc-radius: 8px;
  --qc-chip-radius: 8px;
  --qc-shadow: 0 8px 28px rgba(20, 23, 55, 0.18), 0 2px 6px rgba(20, 23, 55, 0.08);
  /* The reliable native UI stack (matches the host app's norm). Avoids the
     'system-ui' keyword, which can resolve to an inconsistent face. No web font. */
  --qc-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  --qc-mono: ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", Consolas, monospace;
  --qc-focus: 0 0 0 2px #ffffff, 0 0 0 4px var(--qc-accent);
  /* Apply the font on :host so EVERY shadow surface inherits it — the drawer,
     dialog, toast and tooltips are appended to the shadow root (outside .qc-root),
     so without this they fall back to the default serif (Georgia/Times). */
  font-family: var(--qc-font);
  font-size: 14px;
  line-height: 1.55;
  color: var(--qc-text);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.qc-root { font-family: var(--qc-font); font-size: 14px; line-height: 1.55; color: var(--qc-text); }
[hidden] { display: none !important; }
button { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; }

/* Focus: 2px ring, ≥3:1, with a white spacer so it survives on any surface.
   WCAG 2.4.11/2.4.13 — never rely on the UA default on coloured surfaces. */
:where(button, a, textarea, [tabindex], input, summary):focus-visible {
  outline: 2px solid transparent; /* placeholder for forced-colors */
  box-shadow: var(--qc-focus);
  border-radius: 6px;
}

/* ---------- picker highlight layer (aria-hidden; never in a11y tree) ---------- */
.qc-layer { position: fixed; inset: 0; pointer-events: none; z-index: 10; }
.qc-hl {
  position: fixed;
  border: 2px solid var(--qc-highlight);
  background: var(--qc-highlight-fill);
  box-shadow: 0 0 0 1px rgba(255,255,255,0.9);
  border-radius: 3px;
  transition: opacity 120ms ease-out;
}
.qc-hl.qc-locked { background: transparent; }
.qc-hl.qc-lost { border-style: dashed; border-color: var(--qc-text-dim); box-shadow: none; background: transparent; }
.qc-hl.qc-flash { animation: qc-flash 2s ease-out forwards; }
@keyframes qc-flash { 0%,70% { opacity: 1; } 100% { opacity: 0; } }
/* Element tooltip — offset OUTSIDE the element bounds so it never occludes it. */
.qc-tag {
  position: fixed;
  font-family: var(--qc-mono); font-size: 12px;
  background: var(--qc-text); color: #fff;
  border-radius: 6px; padding: 3px 8px; white-space: nowrap;
  box-shadow: var(--qc-shadow);
}

/* ---------- spotlight scrim: dims page except a hole around the pick ---------- */
.qc-spot {
  position: fixed; pointer-events: none; z-index: 9;
  border-radius: 6px;
  box-shadow: 0 0 0 9999px rgba(20, 23, 55, 0.32);
  transition: opacity 150ms ease-out;
}

/* ---------- marker ink layer (z 15, above highlight, below drawer) ---------- */
.qc-ink { position: fixed; inset: 0; z-index: 15; display: none; pointer-events: auto; touch-action: none; }
.qc-ink-chip {
  position: fixed; z-index: 16; display: flex; align-items: center; gap: 4px;
  background: var(--qc-surface); border: 1px solid var(--qc-border);
  border-radius: 999px; padding: 4px; box-shadow: var(--qc-shadow); pointer-events: auto;
}
.qc-ink-confirm {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--qc-accent-strong); color: var(--qc-on-accent); font-weight: 700;
  border-radius: 999px; padding: 6px 12px; min-height: 32px;
}
.qc-ink-confirm svg { width: 14px; height: 14px; }

/* ---------- toolkit drawer (z 20) ---------- */
.qc-drawer {
  position: fixed; top: 0; bottom: 0; width: 320px; z-index: 20;
  background: var(--qc-canvas);
  border: 0 solid var(--qc-divider);
  display: flex; flex-direction: column;
  pointer-events: auto;
  box-shadow: var(--qc-shadow);
  transition: transform 200ms ease-out;
}
.qc-drawer.qc-dock-right { right: 0; border-left-width: 1px; }
.qc-drawer.qc-dock-left { left: 0; border-right-width: 1px; }
.qc-drawer.qc-ghost { opacity: 0.35; pointer-events: none; }
.qc-drawer .qc-drawer-full { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.qc-drawer .qc-rail { display: none; }
.qc-drawer.qc-collapsed { width: 48px; }
.qc-drawer.qc-collapsed .qc-drawer-full { display: none; }
.qc-drawer.qc-collapsed .qc-rail { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 10px 0; }
.qc-rail .qc-rail-count { font-family: var(--qc-mono); font-size: 12px; color: var(--qc-text-dim); min-width: 24px; text-align: center; }
.qc-rail .qc-iconbtn.qc-active { color: var(--qc-accent); }

.qc-drawer-head {
  display: flex; align-items: center; gap: 8px;
  padding: 14px 14px; border-bottom: 1px solid var(--qc-divider);
  background: var(--qc-surface);
}
.qc-drawer-head .qc-title { font-size: 15px; font-weight: 700; flex: 1; letter-spacing: -0.01em; }
.qc-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--qc-text-dim); flex: none; box-shadow: 0 0 0 3px transparent; }
.qc-dot.qc-armed { background: #00a344; box-shadow: 0 0 0 3px rgba(0,163,68,0.18); animation: qc-pulse 2s ease-in-out infinite; }
@keyframes qc-pulse { 0%,100% { opacity: 1; } 50% { opacity: .55; } }

.qc-iconbtn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; min-width: 32px; border-radius: 8px; color: var(--qc-text-dim);
  transition: background 120ms, color 120ms;
}
.qc-iconbtn:hover { background: var(--qc-surface-2); color: var(--qc-text); }
.qc-iconbtn svg { width: 18px; height: 18px; }
.qc-iconbtn[aria-pressed="true"] { background: var(--qc-accent-tint); color: var(--qc-accent-strong); }

.qc-section { padding: 12px 14px; border-bottom: 1px solid var(--qc-divider); }
.qc-section-label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; color: var(--qc-text-dim); text-transform: uppercase; margin-bottom: 8px; }
.qc-tool {
  position: relative;
  display: flex; align-items: center; gap: 10px; width: 100%;
  min-height: 44px; padding: 8px 10px; border-radius: var(--qc-radius);
  text-align: left; color: var(--qc-text); transition: background 120ms;
}
.qc-tool:hover { background: var(--qc-surface-2); }
.qc-tool svg { width: 18px; height: 18px; flex: none; }
.qc-tool .qc-tool-name { flex: 1; }
.qc-tool.qc-active { background: var(--qc-accent-tint); box-shadow: inset 0 0 0 1.5px var(--qc-accent); }
.qc-tool[aria-disabled="true"] { color: var(--qc-text-dim); cursor: default; }
.qc-tool[aria-disabled="true"]:hover { background: none; }
.qc-badge {
  font-size: 12px; padding: 1px 8px; border-radius: 6px; min-height: 20px; display: inline-flex; align-items: center;
  background: var(--qc-surface-2); color: var(--qc-text-dim); border: 1px solid var(--qc-divider);
}
.qc-tool.qc-active .qc-badge { color: #fff; background: var(--qc-accent-strong); border-color: var(--qc-accent-strong); }

.qc-session { flex: 1; overflow-y: auto; }
.qc-session-empty { color: var(--qc-text-dim); padding: 8px 10px; }
.qc-session-empty.qc-draft { color: var(--qc-accent-strong); font-weight: 600; }
.qc-row-wrap { display: flex; align-items: stretch; gap: 2px; }
.qc-row-wrap .qc-row { flex: 1; }
.qc-row {
  display: flex; flex-direction: column; gap: 3px; width: 100%;
  padding: 8px 10px; border-radius: var(--qc-radius);
  text-align: left; color: var(--qc-text); transition: background 120ms;
}
.qc-row:hover { background: var(--qc-surface-2); }
.qc-row-top { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.qc-tagpill { font-size: 11px; font-weight: 600; padding: 1px 7px; border-radius: 5px; border: 1px solid var(--qc-divider); }
.qc-tagpill.qc-type { background: var(--qc-accent-tint); color: var(--qc-accent-strong); border-color: var(--qc-accent); }
.qc-tagpill.qc-sev { display: inline-flex; align-items: center; gap: 4px; }
.qc-sevdot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.qc-sev-blocker .qc-sevdot { background: var(--qc-danger); } .qc-sev-blocker { color: var(--qc-danger); }
.qc-sev-major .qc-sevdot { background: var(--qc-warn); } .qc-sev-major { color: var(--qc-warn); }
.qc-sev-minor .qc-sevdot { background: var(--qc-accent); } .qc-sev-minor { color: var(--qc-accent-strong); }
.qc-sev-trivial .qc-sevdot { background: var(--qc-text-dim); } .qc-sev-trivial { color: var(--qc-text-dim); }
.qc-row-id { font-family: var(--qc-mono); font-size: 11px; color: var(--qc-text-dim); }
.qc-row-desc { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qc-row-sel { font-family: var(--qc-mono); font-size: 11px; color: var(--qc-text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qc-row-copy { align-self: center; opacity: 0; transition: opacity 120ms; }
.qc-row-wrap:hover .qc-row-copy, .qc-row-copy:focus-visible { opacity: 1; }
.qc-row-wrap.qc-new .qc-row { animation: qc-rowflash 1s ease-out; }
@keyframes qc-rowflash { 0% { background: var(--qc-accent-tint); } 100% { background: transparent; } }

.qc-drawer-foot { padding: 12px 14px; border-top: 1px solid var(--qc-divider); background: var(--qc-surface); }
.qc-hint { font-size: 11px; color: var(--qc-text-dim); margin-bottom: 8px; line-height: 1.7; }
.qc-hint kbd { font-family: var(--qc-mono); font-size: 10px; padding: 1px 5px; border: 1px solid var(--qc-border); border-radius: 4px; background: var(--qc-surface); color: var(--qc-text); }
.qc-cta {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; min-height: 44px; border-radius: var(--qc-radius);
  background: var(--qc-accent-strong); color: var(--qc-on-accent); font-weight: 700;
  transition: filter 120ms;
}
.qc-cta:hover { filter: brightness(1.06); }
.qc-cta svg { width: 16px; height: 16px; }
.qc-cta.qc-secondary { background: var(--qc-surface); color: var(--qc-text); border: 1px solid var(--qc-border); }

/* ---------- report dialog (z 30) ---------- */
.qc-dialog {
  position: fixed; width: 400px; max-width: calc(100vw - 24px); z-index: 30;
  background: var(--qc-surface); border: 1px solid var(--qc-divider);
  border-radius: 12px; box-shadow: var(--qc-shadow);
  display: flex; flex-direction: column; max-height: calc(100vh - 24px);
  animation: qc-in 180ms ease-out;
}
@keyframes qc-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
.qc-dialog-head { display: flex; align-items: flex-start; gap: 8px; padding: 14px 16px 8px; border-bottom: 1px solid var(--qc-divider); flex: none; }
.qc-dialog-head .qc-head-txt { flex: 1; min-width: 0; }
.qc-dialog-title { font-size: 15px; font-weight: 700; display: flex; align-items: center; gap: 7px; color: var(--qc-text); }
.qc-dialog-title svg { width: 16px; height: 16px; color: var(--qc-accent-strong); }
.qc-crumb-wrap { margin-top: 6px; }
.qc-crumb-hint { font-family: var(--qc-mono); font-size: 10px; color: var(--qc-text-dim); margin-bottom: 3px; }
.qc-crumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 3px; }
.qc-crumb {
  font-family: var(--qc-mono); font-size: 11px; color: var(--qc-text-dim);
  padding: 3px 6px; min-height: 24px; border-radius: 5px; border: 1px solid transparent;
  display: inline-flex; align-items: center; transition: background 120ms, color 120ms;
}
.qc-crumb:hover { background: var(--qc-surface-2); color: var(--qc-text); }
.qc-crumb.qc-current { color: var(--qc-accent-strong); border-color: var(--qc-accent); background: var(--qc-accent-tint); font-weight: 700; }
.qc-crumb-sep { color: var(--qc-border); }
.qc-idchip { font-family: var(--qc-mono); font-size: 11px; color: var(--qc-text); background: var(--qc-surface-2); border: 1px solid var(--qc-divider); border-radius: 6px; padding: 3px 8px; margin-top: 6px; display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qc-lostnote { font-size: 11px; color: var(--qc-warn); margin-top: 4px; font-weight: 600; }

/* flex:1 + min-height:0 lets the body shrink and scroll inside the capped dialog,
   so the head and foot (Cancel/Submit) stay pinned and visible on tall forms. */
.qc-dialog-body { padding: 12px 16px; display: flex; flex-direction: column; gap: 14px; overflow-y: auto; flex: 1 1 auto; min-height: 0; }
.qc-field label, .qc-fieldset legend { display: block; font-weight: 700; margin-bottom: 5px; color: var(--qc-text); padding: 0; }
.qc-req { color: var(--qc-danger); margin-left: 2px; }
.qc-help { font-size: 12px; color: var(--qc-text-dim); margin: -2px 0 6px; }
textarea.qc-input {
  width: 100%; min-height: 56px; resize: vertical; display: block;
  font: inherit; color: var(--qc-text);
  background: var(--qc-surface); border: 1.5px solid var(--qc-border);
  border-radius: 8px; padding: 8px 10px;
}
textarea.qc-input:hover { border-color: var(--qc-text-dim); }
textarea.qc-input::placeholder { color: var(--qc-text-dim); }
.qc-counter { font-family: var(--qc-mono); font-size: 11px; color: var(--qc-text-dim); text-align: right; margin-top: 3px; }
.qc-counter.qc-over { color: var(--qc-danger); font-weight: 700; }

.qc-fieldset { border: 0; }
.qc-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.qc-chip {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 34px; padding: 6px 13px; border-radius: var(--qc-chip-radius);
  background: var(--qc-surface); border: 1.5px solid var(--qc-border); color: var(--qc-text); font-weight: 600;
  transition: border-color 120ms, background 120ms, color 120ms;
}
.qc-chip:hover { background: var(--qc-accent-tint); border-color: var(--qc-accent); }
.qc-chip .qc-check { width: 15px; height: 15px; display: none; }
.qc-chip[aria-checked="true"] { background: var(--qc-accent-strong); border-color: var(--qc-accent-strong); color: #fff; font-weight: 700; }
.qc-chip[aria-checked="true"] .qc-check { display: inline-flex; }

.qc-seg { display: flex; flex-wrap: wrap; gap: 8px; }
.qc-seg button {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 32px; padding: 5px 12px; border-radius: 8px; font-weight: 600;
  color: var(--qc-text); background: var(--qc-surface); border: 1.5px solid var(--qc-border);
  transition: border-color 120ms, background 120ms, color 120ms;
}
.qc-seg button:hover { background: var(--qc-accent-tint); border-color: var(--qc-accent); }
.qc-seg button .qc-check { width: 15px; height: 15px; display: none; }
.qc-seg button[aria-checked="true"] { background: var(--qc-accent-strong); border-color: var(--qc-accent-strong); color: #fff; font-weight: 700; }
.qc-seg button[aria-checked="true"] .qc-check { display: inline-flex; }
.qc-seg button[aria-checked="true"] .qc-sevdot { box-shadow: 0 0 0 1px #fff; }

/* ---------- captured context (always visible list, per-item exclude) ---------- */
/* flex:none is load-bearing: overflow:hidden makes this box's automatic minimum
   size 0 (the textareas are protected by their explicit min-height), so as a flex
   item of the column-flex body it would otherwise absorb all the overflow and be
   crushed to its borders, clipping every row. It sizes to content instead, and the
   list below caps itself and scrolls rather than running the dialog off-screen. */
.qc-ctxbox { border: 1px solid var(--qc-divider); border-radius: 8px; overflow: hidden; flex: none; }
.qc-ctxhead { display: flex; align-items: center; gap: 6px; padding: 8px 10px; background: var(--qc-surface-2); font-weight: 700; font-size: 12px; }
/* icons carry a viewBox but no intrinsic size — unsized, this one inflated to the
   full width of the box and dragged the dialog off-screen. */
.qc-ctxhead svg { width: 15px; height: 15px; flex: none; color: var(--qc-accent-strong); }
.qc-ctxhead .qc-badge { margin-left: auto; }
.qc-ctxlist { list-style: none; max-height: min(180px, 30vh); overflow-y: auto; overscroll-behavior: contain; }
.qc-ctxitem { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-top: 1px solid var(--qc-divider); }
.qc-ctxitem .qc-ctxname { font-weight: 600; min-width: 84px; }
.qc-ctxitem .qc-ctxval { font-family: var(--qc-mono); font-size: 11px; color: var(--qc-text-dim); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qc-ctxtoggle { margin-left: auto; }
.qc-shot { padding: 8px 10px; border-top: 1px solid var(--qc-divider); }
.qc-shot img { max-width: 100%; max-height: 120px; border: 1px solid var(--qc-border); border-radius: 6px; display: block; }
.qc-shot-actions { display: flex; gap: 10px; margin-top: 6px; }
.qc-shot-actions button { font-size: 12px; color: var(--qc-accent-strong); font-weight: 600; text-decoration: underline; min-height: 24px; }

/* switch (screenshot include, context excludes) — real switch semantics */
.qc-switch { position: relative; width: 36px; height: 20px; border-radius: 999px; background: var(--qc-surface-2); border: 1.5px solid var(--qc-border); flex: none; transition: background 120ms, border-color 120ms; }
.qc-switch::after { content: ""; position: absolute; top: 1px; left: 1px; width: 14px; height: 14px; border-radius: 50%; background: var(--qc-text-dim); transition: transform 120ms, background 120ms; }
.qc-switch[aria-checked="true"] { background: var(--qc-accent-strong); border-color: var(--qc-accent-strong); }
.qc-switch[aria-checked="true"]::after { transform: translateX(16px); background: #fff; }

.qc-error { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; color: var(--qc-danger); font-size: 12px; border: 1.5px solid var(--qc-danger); border-radius: 8px; padding: 8px 10px; background: #fdf2f0; }
.qc-error svg { width: 15px; height: 15px; flex: none; }
.qc-error .qc-link { text-decoration: underline; color: var(--qc-danger); font-weight: 700; min-height: 24px; }

.qc-dialog-foot { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 10px; padding: 12px 16px; border-top: 1px solid var(--qc-divider); flex: none; }
.qc-foot-hint { font-size: 11px; color: var(--qc-text-dim); margin-right: auto; }
.qc-foot-hint kbd { font-family: var(--qc-mono); font-size: 10px; padding: 1px 5px; border: 1px solid var(--qc-border); border-radius: 4px; background: var(--qc-surface); }
.qc-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 40px; padding: 8px 16px; border-radius: 8px; font-weight: 700; transition: filter 120ms, background 120ms; }
.qc-btn svg { width: 16px; height: 16px; }
.qc-btn-ghost { color: var(--qc-text); border: 1px solid var(--qc-border); background: var(--qc-surface); }
.qc-btn-ghost:hover { background: var(--qc-surface-2); }
.qc-btn-submit { background: var(--qc-accent-strong); color: var(--qc-on-accent); }
.qc-btn-submit:hover { filter: brightness(1.06); }
.qc-btn-submit[disabled] { filter: saturate(0.4) brightness(1.1); opacity: 0.7; cursor: default; }
.qc-spin { animation: qc-rot 0.8s linear infinite; display: inline-flex; }
@keyframes qc-rot { to { transform: rotate(360deg); } }
.qc-confirm-discard { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; width: 100%; font-size: 13px; color: var(--qc-text); font-weight: 600; }
.qc-confirm-discard .qc-spacer { margin-right: auto; }
.qc-confirm-discard button { text-decoration: underline; font-weight: 700; min-height: 24px; }
.qc-confirm-discard .qc-discard-yes { color: var(--qc-danger); }

/* ---------- toast (z 40) with undo ---------- */
.qc-toast {
  position: fixed; bottom: 18px; left: 50%; transform: translateX(-50%); z-index: 40;
  display: flex; align-items: center; gap: 12px;
  background: var(--qc-surface); border: 1px solid var(--qc-divider);
  border-radius: 10px; padding: 10px 16px; box-shadow: var(--qc-shadow); color: var(--qc-text);
  animation: qc-in 180ms ease-out; max-width: calc(100vw - 24px);
}
.qc-toast .qc-toast-ic { width: 18px; height: 18px; color: #00a344; flex: none; }
.qc-toast .qc-toast-id { font-family: var(--qc-mono); font-size: 12px; color: var(--qc-text-dim); }
.qc-toast button { color: var(--qc-accent-strong); font-size: 13px; font-weight: 700; min-height: 32px; padding: 0 8px; }
.qc-toast .qc-undobar { height: 2px; background: var(--qc-accent); position: absolute; left: 0; bottom: 0; border-radius: 0 0 10px 10px; animation: qc-countdown 5s linear forwards; }
@keyframes qc-countdown { from { width: 100%; } to { width: 0; } }

/* visually hidden live region */
.qc-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; padding: 0; }

/* tooltip (disabled tool / header) */
.qc-tt { position: relative; }

/* bottom-sheet + reflow: full-width single column under 640px / high zoom */
.qc-dialog.qc-sheet {
  left: 0 !important; right: 0 !important; top: auto !important; bottom: 0;
  width: 100%; max-width: 100%; max-height: 80vh;
  border-radius: 12px 12px 0 0;
  animation: qc-sheet-in 180ms ease-out;
}
@keyframes qc-sheet-in { from { transform: translateY(100%); } to { transform: none; } }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; }
  .qc-toast .qc-undobar { animation: none; width: 100%; }
}

/* Forced-colors / Windows High Contrast: convey state via borders + system colours,
   never background fill alone. */
@media (forced-colors: active) {
  .qc-drawer, .qc-dialog, .qc-toast { border: 1px solid CanvasText; }
  .qc-chip, .qc-seg button, textarea.qc-input, .qc-switch, .qc-btn, .qc-crumb, .qc-tool { border: 1px solid ButtonText; }
  .qc-chip[aria-checked="true"], .qc-seg button[aria-checked="true"], .qc-tool.qc-active, .qc-iconbtn[aria-pressed="true"] { outline: 2px solid Highlight; outline-offset: -2px; }
  .qc-switch[aria-checked="true"] { outline: 2px solid Highlight; outline-offset: 1px; }
  .qc-btn-submit, .qc-cta { border: 1px solid ButtonText; }
  :where(button, a, textarea, [tabindex], summary):focus-visible { outline: 3px solid Highlight; box-shadow: none; }
  .qc-hl { border-color: Highlight; }
}
`
